// src/components/admin_panel/AdminSignupAnalytics.js
import React, { useState, useEffect, useCallback } from "react";

const MOBILE_BREAKPOINT_PX = 700;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT_PX
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const StatRow = ({ label, value, valueStyle }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "baseline",
    padding: "6px 0", borderBottom: "1px solid #f3f4f6", fontSize: "13px", gap: "12px",
  }}>
    <span style={{ color: "#6b7280" }}>{label}</span>
    <span style={{ fontWeight: "600", textAlign: "right", ...valueStyle }}>{value}</span>
  </div>
);

const MobileStatCard = ({ title, children }) => (
  <div style={{
    border: "1px solid #e5e7eb", borderRadius: "10px", padding: "12px 14px", marginBottom: "10px",
  }}>
    <div style={{ fontWeight: "600", marginBottom: "4px" }}>{title}</div>
    {children}
  </div>
);

const SummaryStatTile = ({ label, value, color, sublabel }) => (
  <div style={{
    background: "white", borderRadius: "12px", padding: "16px 18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  }}>
    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>{label}</div>
    <div style={{ fontSize: "24px", fontWeight: "700", color: color || "#111827" }}>{value}</div>
    {sublabel && <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>{sublabel}</div>}
  </div>
);

const REPEAT_COLOR = "#2563eb";
const FIRST_TIME_COLOR = "#10b981";

const AdminSignupAnalytics = ({ adminToken }) => {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/admin/analytics/signup-analytics", {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `HTTP error! status: ${response.status}`);
      }
      setData(await response.json());
    } catch (err) {
      console.error("Error fetching signup analytics:", err);
      setError(err.message || "Failed to load Signup Analytics");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ marginBottom: "10px", color: "#111827" }}>
        🎟️ Signup Analytics
      </h2>
      <p style={{ color: "#666", marginBottom: "12px", fontSize: "14px" }}>
        How many event signups (members clicking Attend on a theme landing page) each Meetup
        group is producing, and whether they're coming from new or returning attendees. A row
        only exists while someone is actively attending, so un-RSVPing removes it — every signup
        counted here is currently active.
      </p>

      <p style={{
        color: "#374151",
        marginBottom: "12px",
        fontSize: "13px",
        background: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: "8px",
        padding: "10px 14px",
      }}>
        📍 Grouped by the <strong>Meetup group the signed-up event itself belongs to</strong> — a
        different attribution than the Meetup Group Finance tab, which groups by where a
        subscriber originally signed up from. Here, a user who's signed up for events under two
        different groups is counted under both, which is what makes "Signed Up For Multiple
        Groups" meaningful.
      </p>

      <p style={{
        color: "#6b7280",
        marginBottom: "20px",
        fontSize: "13px",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        padding: "10px 14px",
      }}>
        ℹ️ Signup tracking started recently, so "Signups Ever" reflects everything since then, not
        the platform's full history. "First-Time" vs "Repeat" is based on a user's total lifetime
        signups (1 vs 2+) across every group, not the chronological order of individual signups.
      </p>

      {loading && (
        <div style={{ padding: "40px", textAlign: "center", fontSize: "18px", color: "#666" }}>
          Loading...
        </div>
      )}

      {!loading && error && (
        <div style={{
          background: "#fee2e2", border: "1px solid #ef4444", borderRadius: "8px",
          padding: "16px", color: "#991b1b",
        }}>
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <div>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px",
            marginBottom: "28px",
          }}>
            <SummaryStatTile label="Signups Ever" value={data.overall.signupsEver} />
            <SummaryStatTile
              label="Signups (Last 30 Days)"
              value={data.overall.signupsLast30Days}
            />
            <SummaryStatTile
              label="First-Time Signups"
              value={data.overall.firstTimeSignups}
              color={FIRST_TIME_COLOR}
              sublabel="User's only signup ever"
            />
            <SummaryStatTile
              label="Repeat Signups"
              value={data.overall.repeatSignups}
              color={REPEAT_COLOR}
              sublabel="From users with 2+ signups ever"
            />
            <SummaryStatTile label="Active Attendees" value={data.overall.activeUsers} />
            <SummaryStatTile label="Avg Signups / Attendee" value={data.overall.avgSignupsPerUser} />
            <SummaryStatTile
              label="Signed Up For Multiple Groups"
              value={data.overall.multiGroupUsers}
              sublabel="Users active in 2+ Meetup groups"
            />
          </div>

          {data.groups.length === 0 && (
            <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
              No signups recorded yet.
            </div>
          )}

          {data.groups.length > 0 && (
            isMobile ? (
              <div>
                {data.groups.map((g) => (
                  <MobileStatCard key={g.group} title={g.group}>
                    <StatRow label="Signups Ever" value={g.signupsEver} />
                    <StatRow label="Signups (Last 30 Days)" value={g.signupsLast30Days} />
                    <StatRow label="First-Time" value={g.firstTimeSignups} valueStyle={{ color: FIRST_TIME_COLOR }} />
                    <StatRow label="Repeat" value={g.repeatSignups} valueStyle={{ color: REPEAT_COLOR }} />
                    <StatRow label="Distinct Attendees" value={g.distinctUsers} />
                    <StatRow label="Avg Signups / Attendee" value={g.avgSignupsPerUser} />
                    <StatRow label="From Multi-Group Attendees" value={g.multiGroupUserSignups} />
                  </MobileStatCard>
                ))}
              </div>
            ) : (
              <div style={{
                background: "white", borderRadius: "12px", padding: "20px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)", overflowX: "auto",
              }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", minWidth: "900px" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                      <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>Meetup Group</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>Signups Ever</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>Last 30 Days</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: FIRST_TIME_COLOR }}>First-Time</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: REPEAT_COLOR }}>Repeat</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>Distinct Attendees</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>Avg / Attendee</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>From Multi-Group Attendees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.groups.map((g) => (
                      <tr key={g.group} style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "10px 12px", fontWeight: "500", whiteSpace: "nowrap" }}>
                          {g.group === "Unaffiliated"
                            ? <span style={{ color: "#9ca3af", fontStyle: "italic" }}>Unaffiliated</span>
                            : g.group}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{g.signupsEver}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{g.signupsLast30Days}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center", color: FIRST_TIME_COLOR }}>{g.firstTimeSignups}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center", color: REPEAT_COLOR }}>{g.repeatSignups}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{g.distinctUsers}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{g.avgSignupsPerUser}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>{g.multiGroupUserSignups}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSignupAnalytics;
